import { useEffect } from "react";

/*
 * Seo, per-page SEO za javne (portal) stranice.
 *
 * SPA nema SSR, pa <head> tagove menjamo direktno kroz useEffect. Social
 * crawleri su već pokriveni PHP shim-om (public/og.php), ovo cilja Google
 * (koji izvršava JS) i naslov taba / adresne linije.
 *
 * Sve props su opcione; kada neka nije prosleđena, koristi se podrazumevana
 * vrednost. Na unmount se vraćaju podrazumevane vrednosti iz index.html.
 */

const ORIGIN = "https://mrc.rs";

// Podrazumevane vrednosti, moraju odgovarati public/index.html
const DEFAULTS = {
  title: "МРЧ - Official Merch Store | Bendovi merch",
  description:
    "МРЧ - Zvanična online prodavnica merch proizvoda bendova. Otkrijte ekskluzivne majice, dukseve, aksesoare i pratite najnovije vesti sa muzičke scene.",
  ogTitle: "МРЧ - Official Merch Store",
  ogDescription:
    "Zvanična online prodavnica merch proizvoda bendova. Otkrijte ekskluzivne majice, dukseve, aksesoare i pratite najnovije vesti sa muzičke scene.",
  twitterDescription: "Zvanična online prodavnica merch proizvoda bendova.",
  url: ORIGIN,
  image: `${ORIGIN}/logo.png`,
  type: "website",
};

/* Relativnu putanju ("/bend/3") pretvara u apsolutan URL na mrc.rs */
export const toAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${ORIGIN}${url.startsWith("/") ? "" : "/"}${url}`;
};

/* Skida HTML tagove, sažima razmake i skraćuje tekst na ~max karaktera
   (na granici reči), za meta opise iz sadržaja proizvoda/novosti/bio */
export const seoExcerpt = (text, max = 160) => {
  if (!text) return null;
  const plain = String(text)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return null;
  if (plain.length <= max) return plain;
  const cut = plain.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 60 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

/* Upsert <meta> taga po atributu identifikacije (name ili property) */
const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

/* Upsert/uklanjanje <link rel="canonical"> (index.html ga nema, pa se
   bez href-a tag uklanja umesto da ostane pogrešan) */
const setCanonicalTag = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/* Upsert/uklanjanje JSON-LD skripte, uvek najviše jedna, označena data-seo */
const setJsonLdTag = (json) => {
  let el = document.head.querySelector('script[data-seo="true"]');
  if (!json) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo", "true");
    document.head.appendChild(el);
  }
  el.textContent = json;
};

/* Primeni kompletan skup vrednosti na sve tagove koje komponenta vodi */
const applyTags = (values) => {
  document.title = values.title;
  setMeta("name", "description", values.description);
  setMeta("property", "og:title", values.ogTitle);
  setMeta("property", "og:description", values.ogDescription);
  setMeta("property", "og:url", values.url);
  setMeta("property", "og:type", values.type);
  setMeta("property", "og:image", values.image);
  setMeta("name", "twitter:title", values.ogTitle);
  setMeta("name", "twitter:description", values.twitterDescription);
  setCanonicalTag(values.canonical);
  setJsonLdTag(values.jsonLd);
};

/* Podrazumevano stanje, kao u index.html (canonical i JSON-LD ne postoje) */
const defaultTags = () => ({
  title: DEFAULTS.title,
  ogTitle: DEFAULTS.ogTitle,
  description: DEFAULTS.description,
  ogDescription: DEFAULTS.ogDescription,
  twitterDescription: DEFAULTS.twitterDescription,
  url: DEFAULTS.url,
  image: DEFAULTS.image,
  type: DEFAULTS.type,
  canonical: null,
  jsonLd: null,
});

/**
 * <Seo title description canonical image type jsonLd />
 *
 * - title:       "Naziv stranice" → tab prikazuje "Naziv stranice | МРЧ"
 * - description: meta + og + twitter opis
 * - canonical:   relativna putanja ("/prodavnica") → apsolutni canonical + og:url
 * - image:       og:image (relativna putanja se prefiksuje sa https://mrc.rs)
 * - type:        og:type (podrazumevano "website")
 * - jsonLd:      objekat ili niz → <script type="application/ld+json">
 */
const Seo = ({ title, description, canonical, image, type, jsonLd }) => {
  // Serijalizovan JSON kao dependency, inline objekti/nizovi se prave
  // iznova na svakom renderu i ne smeju svaki put pokretati effect
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    const canonicalUrl = canonical ? toAbsoluteUrl(canonical) : null;
    applyTags({
      ...defaultTags(),
      title: title ? `${title} | МРЧ` : DEFAULTS.title,
      // og/twitter naslov bez "| МРЧ" sufiksa (kao og.php shim)
      ogTitle: title || DEFAULTS.ogTitle,
      ...(description
        ? {
            description,
            ogDescription: description,
            twitterDescription: description,
          }
        : {}),
      url: canonicalUrl || DEFAULTS.url,
      image: toAbsoluteUrl(image) || DEFAULTS.image,
      type: type || DEFAULTS.type,
      canonical: canonicalUrl,
      jsonLd: jsonLdString,
    });
  }, [title, description, canonical, image, type, jsonLdString]);

  // Na unmount vrati podrazumevane vrednosti iz index.html
  useEffect(() => () => applyTags(defaultTags()), []);

  return null;
};

export default Seo;
